import {useMemo, useState} from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {DraggableList} from 'shells/draggable-row/draggable-list';
import {reorder} from 'shells/draggable-row/helpers';
import {Label} from 'shells/form/label';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {CustomFieldListItem} from 'shells/list-item/custom-field-list-item';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import type {Widget} from 'shells/widgets/types';

import type {EntityType} from 'nutshell-graphql-types';

import {GraphQLSelectCustomFields} from '../../core/select/graphql-select-custom-fields';
import type {Option} from '../../core/select/graphql-select-inline';

import {CreateOrEditWidgetFooter} from './create-or-edit-widget-footer';

export const MAX_FIELDS = 50;

type Props = {
    widget: Widget | null | undefined;
    entityType: EntityType;
    onSave: (name: string, customFields: Array<Option>) => void;
};

export function CreateOrEditWidgetForm(props: Props) {
    const [name, setName] = useState<string>(props.widget ? props.widget.name : '');
    const [selectedCustomFields, setSelectedCustomFields] = useState<Array<Option>>(
        props.widget && props.widget.customWidget
            ? props.widget.customWidget.fields.map((field) => ({
                  id: field.id,
                  name: field.name,
                  type: field.type,
              }))
            : []
    );

    const handleUpdateCustomFields = (fields: Array<Option> | Option) => {
        if (Array.isArray(fields)) {
            setSelectedCustomFields(fields);
        } else {
            setSelectedCustomFields([fields]);
        }
    };

    const listItems = useMemo(() => {
        if (Array.isArray(selectedCustomFields)) {
            return selectedCustomFields.map((field) => ({
                id: field.id,
                component: (
                    <CustomFieldListItem
                        name={field.name}
                        type={field.type}
                        onRemove={() => {
                            setSelectedCustomFields(
                                selectedCustomFields.filter((value) => value.id !== field.id)
                            );
                        }}
                    />
                ),
            }));
        }

        return [];
    }, [selectedCustomFields]);

    // @ts-expect-error
    const handleReorder = (startIndex, endIndex) => {
        const updatedCustomFields = reorder([...selectedCustomFields], startIndex, endIndex);
        setSelectedCustomFields(updatedCustomFields);
    };

    const anyChangesMade = useMemo(() => {
        if (!props.widget || !props.widget.customWidget) {
            return true;
        }

        if (props.widget.name !== name) {
            return true;
        }

        const widgetFields = props.widget.customWidget.fields;

        if (widgetFields.length !== selectedCustomFields.length) {
            return true;
        }

        // Check if order or content changed
        for (let i = 0; i < widgetFields.length; i++) {
            // @ts-expect-error
            if (widgetFields[i].id !== selectedCustomFields[i].id) {
                return true;
            }
        }

        return false;
    }, [name, selectedCustomFields, props.widget]);

    const toolTip = useMemo(() => {
        if (!name && !selectedCustomFields.length) {
            return 'Name and custom fields are required';
        } else if (!name) {
            return 'Name is required';
        } else if (!selectedCustomFields.length) {
            return 'Custom fields are required';
        } else if (!anyChangesMade) {
            return 'No changes made';
        }

        return undefined;
    }, [name, selectedCustomFields, anyChangesMade]);

    const maxReached = useMemo(() => {
        return selectedCustomFields.length >= MAX_FIELDS;
    }, [selectedCustomFields]);
    const headerText = props.widget ? 'Edit custom field widget' : 'New custom field widget';

    const manageLink = useMemo(() => {
        switch (props.entityType) {
            case 'LEAD':
                return '/setup/custom-field/lead';
            case 'CONTACT':
                return '/setup/custom-field/person';
            case 'ACCOUNT':
                return '/setup/custom-field/company';
        }
    }, [props.entityType]);

    return (
        <>
            <div className='pad-24'>
                <Body size='xlarge' isBold={true} color='dark'>
                    {headerText}
                </Body>
            </div>
            <div className='full-height overflow-auto px-24 pb-24'>
                <Stack spacing={16}>
                    <div>
                        <Label htmlFor='name' required={true}>
                            Name
                        </Label>
                        <TextField
                            // @ts-expect-error
                            name='name'
                            placeholder='Name'
                            onChange={(value) => {
                                setName(value);
                            }}
                            value={name}
                            focusOnMount={true}
                        />
                    </div>
                    <div>
                        <Stack isHorizontal={true} justifyContent='space-between'>
                            <Label htmlFor='custom-fields' required={true}>
                                Custom fields
                            </Label>
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                variant='primary'
                                size='small'
                                to={manageLink}
                            >
                                Manage
                            </Link>
                        </Stack>
                        <GraphQLSelectCustomFields
                            entityType={props.entityType}
                            onChange={handleUpdateCustomFields}
                            selectedValues={selectedCustomFields}
                            isMulti={true}
                            controlShouldRenderValue={false}
                            disabled={maxReached}
                        />
                    </div>
                    <DraggableList
                        handleReorder={handleReorder}
                        listItems={listItems}
                        showOverflow={true}
                        isInModal={true}
                    />
                </Stack>
            </div>
            <CreateOrEditWidgetFooter
                isDisabled={!name || !selectedCustomFields.length || !anyChangesMade}
                onComplete={() => {
                    props.onSave(name, selectedCustomFields);
                }}
                maxReached={maxReached}
                disabledTooltip={toolTip}
            />
        </>
    );
}
